import {request} from '@zoefin/request';

import {redirectToDashboard} from '@/utils/userUtils';

import {createHubspotMeta, getGAClientId} from './bookingUtils';

type RouterQuery = Record<string, string | string[] | undefined>;

const readBrowserQuery = (): RouterQuery => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const out: RouterQuery = {};

  for (const [key, value] of params.entries()) {
    const existing = out[key];

    if (existing === undefined) {
      out[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      out[key] = [existing, value];
    }
  }

  return out;
};

interface UserCreationResponse {
  redirect_url: string;
}

export type PortalMatchProps = (
  options: {
    body: ObjectOfAny;
    testingFormVersion?: string;
  },
  callback: (error?: Error, redirect_url?: string) => void,
) => Promise<void>;

enum HostedParams {
  sub1 = 'sub1', // Bankrate, Morning Download, money.com
  sub2 = 'sub2', // Bankrate, Morning Download
  sub3 = 'sub3',
  sub4 = 'sub4',
  utm_medium = 'utm_medium', // Nerdwallet
  utm_id = 'utm_id', // Nerdwallet
  utm_content = 'utm_content', // Nerdwallet
  subid = 'subid', // Moneywise, Consumer Voice
  source_id = 'source_id', // US News, money.com, investopedia
  click_id = 'click_id', // MoneyTalksNews, MyBankTracker, US News, money.com, investopedia
  utm_source = 'utm_source', // US News,
  transID = 'transID', // Forbes, USA Today, CNN, investor.com & Expertise
  sourceID = 'sourceID', // Forbes, USA Today, CNN, investor.com & Expertise
  pageID = 'pageID', // Forbes, USA Today, CNN, investor.com & Expertise
  PageID = 'PageID', // Fortune Recommends
  clickID = 'clickID', // Betterbuck, FinanceBuzz
  ClickID = 'ClickID', // Comparisons
  AffiliateID = 'AffiliateID', // Comparisons
  subID = 'subID', // Forbes, USA Today, CNN, investor.com & Expertise
  campaign = 'campaign', // Investor.com
  hosted_source = 'hosted_source', // Ours
  source = 'source',
  Clickid = 'Clickid', // Yep Ads, Synergy, Joywallet, Hearst, 24/7 Wallstreet, Morning Download, Prime Women, Moby,
  Affid = 'Affid', // Yep Ads
  Subaffid = 'Subaffid', // Yep Ads
  Subid = 'Subid', // Synergy, Joywallet
  PartnerID = 'PartnerID', // 24/7 Wallstreet
  contentblockid = 'contentblockid', // FinanceBuzz
}

/**
 * Every third party provides custom query params when embedding our form experience
 * we must pass those parameters to the dashboard when the user lands in the match
 */
export const getHostedParams = (routerQuery: RouterQuery = undefined) => {
  const baseQueryParams = routerQuery || readBrowserQuery();

  let query = '';

  // dynamic object creation if query param does not exist don't add it to the object
  const queryObject = Object.values(HostedParams).reduce((acc, param) => {
    if (baseQueryParams[param]) {
      acc[param] = baseQueryParams[param];
    }

    return acc;
  }, {});

  // Build query string using URLSearchParams to avoid leading '&' and drop invalid values
  const searchParams = new URLSearchParams();

  Object.entries(queryObject).forEach(([param, paramValue]) => {
    if (!paramValue) {
      return;
    }

    if (Array.isArray(paramValue)) {
      paramValue.forEach((value) => {
        if (value && value !== 'undefined' && value !== 'null') {
          searchParams.append(param, String(value));
        }
      });

      return;
    }

    if (paramValue && paramValue !== 'undefined' && paramValue !== 'null') {
      searchParams.append(param, String(paramValue));
    }
  });

  query = searchParams.toString();

  return {
    object: queryObject,
    query,
  };
};

export const checkUserStatusAndRedirect = async (email: string) => {
  return new Promise((resolve, reject) => {
    request<{is_in_plutus?: boolean}>(`/api/portal/check-user-status`, {
      method: 'POST',
      body: {email},
    })
      .then((res) => {
        if (res.is_in_plutus) {
          // User exists in Plutus - redirect to dashboard app
          redirectToDashboard(email);

          return;
        }

        resolve(res);
      })
      .catch((e) => reject(e));
  });
};

/**
 * Register user in core and create portal session
 */
export const portalGetMatch: PortalMatchProps = async (
  {body, testingFormVersion},
  callback,
) => {
  await checkUserStatusAndRedirect(body.email);

  const hubspotMeta = createHubspotMeta();
  const clientId = getGAClientId();
  const finalBody = {...body};

  // Tracking parameters from any FAAF embedded in an iframe
  const hostedParams = getHostedParams();

  // Add Google tag Manager "client id" if available
  if (clientId) {
    finalBody.google_client_id = clientId;
  }

  // Sends tracking query parameters to user creation
  finalBody.onboarding_tracking_params = JSON.stringify(hostedParams.object);

  try {
    const {redirect_url} = await request<UserCreationResponse>(
      `/api/referrals/zoe/form`,
      {
        method: 'POST',
        headers: {
          formVersion: testingFormVersion || 'A',
        },
        body: {...finalBody, meta: hubspotMeta},
      },
    );

    const finalRedirectUrl = `${redirect_url}?${hostedParams.query}`;

    window.location.href = finalRedirectUrl;

    callback(undefined, finalRedirectUrl);
  } catch (error) {
    callback(error instanceof Error ? error : new Error(String(error)));
  }
};
